import React from 'react';
import { isHiddenReviewColumn } from '../constants/reviewHiddenColumns';

export type WideTablePayload = {
  available?: boolean;
  table?: string;
  total?: number;
  columns?: string[];
  display_columns?: string[];
  rows?: Record<string, unknown>[];
  team_code?: unknown;
  season?: unknown;
  reason?: string;
};

type Props = {
  title: string;
  payload: WideTablePayload | null;
  loading?: boolean;
  pageSize?: number;
  offset: number;
  onPageChange: (nextOffset: number) => void;
};

function cellValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function ReviewWideStatsTable({
  title,
  payload,
  loading = false,
  pageSize = 25,
  offset,
  onPageChange,
}: Props) {
  const allColumns = payload?.display_columns?.length
    ? payload.display_columns
    : payload?.columns || [];
  const columns = allColumns.filter((col) => !isHiddenReviewColumn(col));
  const hiddenCount = allColumns.length - columns.length;
  const rows = payload?.rows || [];
  const total = payload?.total ?? 0;
  const page = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading {title}…</div>;
  }

  if (payload && payload.available === false) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        {title} unavailable ({payload.reason || 'table_missing'}).
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500">
            Table: <span className="font-mono">{payload?.table || '—'}</span>
            {payload?.team_code != null && (
              <> · team_code: <span className="font-mono">{String(payload.team_code)}</span></>
            )}
            {payload?.season != null && (
              <> · season: <span className="font-mono">{String(payload.season)}</span></>
            )}
            {' · '}{total} row{total === 1 ? '' : 's'}
            {hiddenCount > 0 && (
              <> · <span title="Unmapped metadata columns StatCrew doesn't populate are hidden">{hiddenCount} metadata column{hiddenCount === 1 ? '' : 's'} hidden</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            disabled={offset <= 0}
            onClick={() => onPageChange(Math.max(0, offset - pageSize))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-slate-600">
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={offset + pageSize >= total}
            onClick={() => onPageChange(offset + pageSize)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-slate-50">
            <tr>
              {columns.map((col) => (
                <th key={col} className="whitespace-nowrap border-b border-slate-200 px-2 py-2 text-left font-semibold text-slate-600">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(columns.length, 1)} className="px-4 py-8 text-center text-slate-400">
                  No rows for this job.
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr key={ri} className="border-t border-slate-100 hover:bg-slate-50/80">
                  {columns.map((col) => (
                    <td key={col} className="whitespace-nowrap px-2 py-1.5 text-slate-800">
                      {cellValue(row[col])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
